import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, storeActions } from '../../store'
import { RetorikMainComponentProps, RetorikWidgetProps } from '../../store/models/retorikTypes'
import { ContainerParent } from '../../models/enums'
import { getWidgetBorderStyle, getWidgetButtonStyle } from '../../utils/getWidgetStyles'
import { widgetDefault } from '../../models/colors'
import { WidgetFrame } from '../../models/types'
import CloseIcon from '../Icons/Miscellaneous/CloseIcon'
import ExtendIcon from '../Icons/Miscellaneous/ExtendIcon'

interface LayoutHandlerProps extends RetorikMainComponentProps, Partial<RetorikWidgetProps> {
  children: any
}

const LayoutHandler = (props: LayoutHandlerProps) => {
  const dispatch = useDispatch()
  const translation = useSelector((state: RootState) => state.localeReducer.currentTranslations)
  const currentLayout = useSelector((state: RootState) => state.retorikReducer.currentLayout)
  const displayChangeLayoutButton = useSelector((state: RootState) => state.retorikReducer.configuration).allowSwitchLayout

  const open = useSelector((state: RootState) => state.widgetReducer.open)
  const loaded = useSelector((state: RootState) => state.widgetReducer.loaded)
  const widgetDisplayType = useSelector((state: RootState) => state.widgetReducer.widgetDisplayType)

  const [currentConfiguration, setCurrentConfiguration] = useState<WidgetFrame | undefined>(props.widgetConfig?.large)
  const [widgetButton, setWidgetButton] = useState<string>('')
  const [widgetBorder, setWidgetBorder] = useState<string>('')
  const [mobileWidth, setMobileWidth] = useState<boolean>(window.innerWidth < 600)
  const [frameStyle, setFrameStyle] = useState<Record<string, any>>({})
  const [hover, setHover] = useState<boolean>(false)

  const handleResize = (): void => {
    setMobileWidth(window.innerWidth < 600)
  }

  useEffect(() => {
    setWidgetButton(getWidgetButtonStyle(props.widgetConfig?.button))
    window.addEventListener('resize', handleResize)
    dispatch(storeActions.retorik.setLayout(ContainerParent.widget))

    return (): void => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    mobileWidth
      ? setFrameStyle({
          width: open ? '100%' : '0px',
          height: '100%'
        })
      : setFrameStyle({
          top: currentConfiguration?.position?.top,
          bottom: currentConfiguration?.position?.bottom
            ? currentConfiguration?.position?.bottom
            : currentConfiguration?.position?.top || currentConfiguration?.position?.left || currentConfiguration?.position?.right
            ? undefined
            : '1rem',
          left: currentConfiguration?.position?.left,
          right: currentConfiguration?.position?.right
            ? currentConfiguration?.position?.right
            : currentConfiguration?.position?.top || currentConfiguration?.position?.bottom || currentConfiguration?.position?.left
            ? undefined
            : '1rem',
          borderColor: currentConfiguration?.border?.color || widgetDefault.rgb
        })
  }, [mobileWidth, currentConfiguration])

  useEffect(() => {
    switch (widgetDisplayType) {
      case 'large':
        setCurrentConfiguration(props.widgetConfig?.large)
        setWidgetBorder(getWidgetBorderStyle(props.widgetConfig?.large?.border))
        break
      case 'thumbnail':
        setCurrentConfiguration(props.widgetConfig?.thumbnail || props.widgetConfig?.large)
        setWidgetBorder(getWidgetBorderStyle(props.widgetConfig?.thumbnail?.border || props.widgetConfig?.large?.border))
        break
      default:
        break
    }
  }, [widgetDisplayType])

  const extendButtonHandler = (): void => {
    // if different display type available
    if (props.widgetConfig?.large && props.widgetConfig?.thumbnail) {
      dispatch(storeActions.widget.toggleDisplay())
    } else {
      dispatch(storeActions.retorik.setLayout(ContainerParent.agent))
    }
  }

  return (
    <>
      {props.widgetConfig?.button?.display !== false && (
        <button
          id='retorik-widget-button'
          className={`rf-fixed rf-px-2 rf-flex rf-flex-row rf-items-center ${widgetButton}`}
          style={{
            color: props.widgetConfig?.button?.text?.color || widgetDefault.rgb,
            backgroundColor: hover ? props.widgetConfig?.button?.background?.colorHover || 'transparent' : props.widgetConfig?.button?.background?.color || 'transparent',
            borderWidth: props.widgetConfig?.button?.border?.thickness !== undefined ? `${props.widgetConfig?.button?.border?.thickness}px` : '1px',
            borderColor: hover ? props.widgetConfig?.button?.border?.colorHover || widgetDefault.rgb : props.widgetConfig?.button?.border?.color || widgetDefault.rgb,
            top: props.widgetConfig?.button?.position?.top,
            bottom: props.widgetConfig?.button?.position?.bottom
              ? props.widgetConfig?.button?.position?.bottom
              : props.widgetConfig?.button?.position?.top || props.widgetConfig?.button?.position?.left || props.widgetConfig?.button?.position?.right
              ? undefined
              : '1rem',
            left: props.widgetConfig?.button?.position?.left,
            right: props.widgetConfig?.button?.position?.right
              ? props.widgetConfig?.button?.position?.right
              : props.widgetConfig?.button?.position?.top || props.widgetConfig?.button?.position?.bottom || props.widgetConfig?.button?.position?.left
              ? undefined
              : '1rem'
          }}
          onClick={() => dispatch(storeActions.widget.toggleWidget())}
          onMouseEnter={(): void => setHover(true)}
          onMouseLeave={(): void => setHover(false)}
        >
          {/* Image on the left if there is a URL and either position = 'left' or no position */}
          {props.widgetConfig?.button?.image?.hide !== true && props.widgetConfig?.button?.image?.url && props.widgetConfig?.button?.image?.position !== 'right' && (
            <img
              className={`${props.widgetConfig?.button?.image.marginY === false ? '' : 'rf-my-1'}`}
              src={`${props.widgetConfig.button.image.url}`}
              alt='Retorik widget'
              style={{
                height: props.widgetConfig?.button?.image.height || '2rem'
              }}
            />
          )}
          {/* Text content */}
          <div
            className='rf-py-2 rf-mx-2 rf-leading-4 rf-text-justify rf-whitespace-break-spaces'
            style={{
              overflowWrap: 'break-word',
              maxWidth: props.widgetConfig?.button?.text?.maxWidth || 200
            }}
          >
            {props.widgetConfig?.button?.text?.content || translation.common.needhelp}
          </div>
          {/* Image on the right if there is a URL and position = 'right' */}
          {props.widgetConfig?.button?.image?.hide !== true && props.widgetConfig?.button?.image?.url && props.widgetConfig?.button?.image?.position === 'right' && (
            <img
              className={`${props.widgetConfig?.button?.image.marginY === false ? '' : 'rf-my-1'}`}
              src={`${props.widgetConfig.button.image.url}`}
              alt='Retorik widget'
              style={{
                height: props.widgetConfig?.button?.image.height || '2rem'
              }}
            />
          )}
        </button>
      )}
      {loaded && (
        <div
          className={`${currentLayout === ContainerParent.widget ? 'rf-fixed rf-pt-8 rf-height-fill-available rf-transition-all rf-overflow-hidden rf-max-w-[100vw]' : ''}`}
          style={
            currentLayout === ContainerParent.widget
              ? {
                  ...frameStyle,
                  width: open ? (mobileWidth ? '100%' : currentConfiguration?.width || '400px') : '0px',
                  height: currentConfiguration?.height || (mobileWidth ? '100%' : '630px')
                }
              : {}
          }
          data-testid='widget-wrapper'
        >
          {currentLayout === ContainerParent.widget && (
            <React.Fragment>
              {/* Layout change button */}
              {!!displayChangeLayoutButton && !mobileWidth && (
                <button
                  onClick={extendButtonHandler}
                  aria-label='Extend'
                  className='rf-absolute rf-z-exitwidget rf-flex rf-right-8 rf-top-0 rf-p-1 rf-bg-truewhite rf-border rf-border-trueblack rf-rounded-lg'
                >
                  <ExtendIcon className='rf-h-4 rf-w-4' color='#000000' extended={false} />
                </button>
              )}

              {/* Close button */}
              <button
                onClick={() => dispatch(storeActions.widget.toggleWidget())}
                aria-label='Close'
                className='rf-absolute rf-z-exitwidget rf-flex rf-right-0 rf-top-0 rf-p-1 rf-bg-truewhite rf-border rf-border-trueblack rf-rounded-lg'
              >
                <CloseIcon className='rf-h-4 rf-w-4' color='#000000' />
              </button>
            </React.Fragment>
          )}

          <div
            className={`${currentLayout === ContainerParent.widget ? `rf-relative rf-h-full rf-w-full rf-overflow-hidden ${!mobileWidth && widgetBorder}` : ''}`}
            style={
              currentLayout === ContainerParent.widget
                ? {
                    borderColor: frameStyle.borderColor
                  }
                : {}
            }
          >
            {props.children}
          </div>
        </div>
      )}
    </>
  )
}

export default LayoutHandler
